import { IconCalendar16, IconClock16, IconPinMarker16 } from '@warp-ds/icons/react';
import { Alert, Button, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { type FormEvent, useReducer, useRef, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { t } from '../../locale/texts';
import { safeFormatClientDate } from '../../util/datetime-fns';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { carAutocompleteMapper } from '../common/autocomplete/utils/autocompleteMapper';
import { CustomSelect } from '../common/CustomSelect';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { storeLastSearchCar } from '../common/lastSearch';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { trackSearchClick } from '../common/tracking';
import { AgePicker } from './AgePicker';
import styles from './CarSearch.module.css';
import { CarSearchHiddenInputs } from './CarSearchHiddenInputs';
import actions, { reducer } from './carActions';
import { type CarState, carDefaultState } from './carDefaultState';
import { useCarInitialState } from './hooks/useCarInitialState';
import { useCarValidation } from './hooks/useCarValidation';
import { useLocationResolver } from './hooks/useLocationResolver';
import { useTimePicker } from './hooks/useTimePicker';
import { buildCarTrackingItem } from './utils/buildCarTrackingItem';

type CarSearchProps = {
    prefill?: Partial<CarState>;
    position: string;
    unleash?: Record<string, boolean | string> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const CarSearch = ({ prefill, unleash, position }: CarSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, carDefaultState);

    const isSmallScreen = useBreakpoints({ breakpoint: 'sm' });
    const { carAutocompleteUrl } = useConfig();

    const { resolvePickupLocation, resolveReturnLocation } = useLocationResolver(dispatch);
    const { invalid, validationErrors, getInvalidFields } = useCarValidation(state, pristine);

    useCarInitialState(dispatch, prefill);
    const { allTimes, returnTimeOptions } = useTimePicker({
        pickupDate: state.pickupDate,
        returnDate: state.returnDate,
        pickupTime: state.pickupTime,
        returnTime: state.returnTime,
        onReturnTimeChange: (value) => dispatch(actions.setReturnTime(value)),
        interval: state.interval,
    });

    const returnDateInputRef = useRef<HTMLInputElement>(null);
    const datePickerContainerRef = useRef<HTMLDivElement>(null);

    const formatOptions = (times: string[]) =>
        times.map((time) => ({
            value: time,
            label: time,
            icon: <IconClock16 aria-hidden />,
        }));

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        setPristine(false);

        if (Object.values(getInvalidFields(false)).some(Boolean)) {
            event.preventDefault();
            return;
        }

        trackSearchClick('Search carrental', '/reise/leiebil', 'car_rental', position, buildCarTrackingItem(state), 'Form');
        storeLastSearchCar(state);
    };

    return (
        <SearchPanelWrapper onSubmit={handleSubmit} action="https://leiebil.finn.no/NO/book" method="GET" dataTestId="carSearch">
            <div className={styles['form-header']}>
                <AgePicker value={state.driverAge?.toString() || ''} onChange={(val) => dispatch(actions.setDriverAge(val))} />
                <CustomSelect
                    slim
                    triggerIcon={isSmallScreen ? <IconPinMarker16 /> : undefined}
                    fullscreenTitle="Hvor skal du levere bilen?"
                    data-testid="returnLocationIsSame"
                    value={state.returnLocationIsSame ? 'same' : 'different'}
                    onChange={(e) => dispatch(actions.setReturnLocationIsSame(e === 'same'))}
                    options={[
                        { value: 'same', label: 'Lever samme sted' },
                        { value: 'different', label: 'Annet leveringssted' },
                    ]}
                />
            </div>
            <div className={cn(styles['form-body'], state.returnLocationIsSame && styles['form-body--same-location'])}>
                <div
                    className={cn(styles['form-body__col-wrapper'], {
                        [styles['form-body__col-wrapper--same-location']]: state.returnLocationIsSame,
                    })}>
                    <div className={styles['form-body__col']}>
                        {!state.returnLocationIsSame && <div className={styles['route-divider']}>HENTING</div>}
                        <Autocomplete
                            vertical="car"
                            id="pickupLocation"
                            label={t('car.autocomplete.pickup.label')}
                            placeholder={t('car.autocomplete.pickup.placeholder')}
                            defaultValue={state.pickupLocation}
                            onSelect={(option) => resolvePickupLocation(option)}
                            url={carAutocompleteUrl}
                            mapOptions={carAutocompleteMapper}
                            minChars={3}
                            error={validationErrors.pickupLocation}
                        />
                        <div ref={datePickerContainerRef} className={styles['form-body__date-time']}>
                            <FrontpageDatePickerWrapper
                                className={styles.FrontpageDatePicker}
                                startDate={state.pickupDate}
                                endDate={state.returnDate}
                                useRange={true}
                                hideEndDateInput
                                onDateChange={({ startDate, endDate }) => dispatch(actions.setDates({ startDate, endDate }))}
                                useAnchor={{
                                    title: 'Når skal bilen hentes?',
                                    invalid: invalid.pickupDate,
                                    helpText: validationErrors.pickupDate,
                                }}>
                                <TextField
                                    id="pickupDate"
                                    label="Hentedato"
                                    invalid={invalid.pickupDate}
                                    helpText={validationErrors.pickupDate}
                                />
                                <TextField ref={returnDateInputRef} id="returnDate-hidden" label="Leveringsdato" />
                            </FrontpageDatePickerWrapper>
                            <CustomSelect
                                relative
                                fullscreenTitle="Når skal bilen hentes?"
                                triggerLabel="Hentetid"
                                value={state.pickupTime}
                                onChange={(e) => dispatch(actions.setPickupTime(e))}
                                options={formatOptions(allTimes)}
                                className={styles['time-select']}
                            />
                        </div>
                    </div>
                    <div className={styles['form-body__col']}>
                        {!state.returnLocationIsSame && (
                            <>
                                <div className={styles['route-divider']}>LEVERING</div>
                                <Autocomplete
                                    departure={false}
                                    vertical="car"
                                    id="returnLocation"
                                    label={t('car.autocomplete.return.label')}
                                    placeholder={t('car.autocomplete.return.placeholder')}
                                    defaultValue={state.returnLocation}
                                    onSelect={(option) => resolveReturnLocation(option)}
                                    url={carAutocompleteUrl}
                                    mapOptions={carAutocompleteMapper}
                                    minChars={3}
                                    error={validationErrors.returnLocation}
                                />
                            </>
                        )}
                        <div className={styles['form-body__date-time']}>
                            <div className={cn(styles.FrontpageDatePicker, styles['return-date-trigger'])}>
                                {!isSmallScreen && (
                                    <span aria-hidden="true" className="text-s font-bold pb-4 s-text">
                                        Leveringsdato
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className={cn(
                                        'text-m leading-m py-12 px-8 rounded-4 border-1 s-bg flex items-center gap-8 cursor-pointer focusable focusable-inset focus:border-transparent focus:[--w-outline-offset:-2px]',
                                        invalid.returnDate
                                            ? 's-border-negative focus:[--w-s-color-border-focus:var(--w-s-color-border-negative)]'
                                            : 's-border-strong',
                                    )}
                                    onClick={() => {
                                        if (returnDateInputRef.current) {
                                            returnDateInputRef.current.click();
                                        } else {
                                            datePickerContainerRef.current
                                                ?.querySelector<HTMLButtonElement>('.date-picker__button')
                                                ?.click();
                                        }
                                    }}>
                                    <span className="sr-only">Leveringsdato,</span>
                                    <IconCalendar16 aria-hidden />
                                    <span className={cn(!state.returnDate && 's-text-placeholder')}>
                                        {safeFormatClientDate(state.returnDate) || 'dd.mm.åååå'}
                                    </span>
                                </button>
                                {validationErrors.returnDate && (
                                    <span className="text-xs s-text-negative">{validationErrors.returnDate}</span>
                                )}
                            </div>
                            <CustomSelect
                                relative
                                fullscreenTitle="Når skal bilen leveres?"
                                triggerLabel="Leveringstid"
                                value={state.returnTime}
                                onChange={(e) => dispatch(actions.setReturnTime(e))}
                                options={formatOptions(returnTimeOptions)}
                                className={styles['time-select']}
                            />
                        </div>
                    </div>
                </div>
                <Button
                    fullWidth
                    primary
                    type="submit"
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}>
                    {t('action.search')}
                </Button>
            </div>
            <Alert type="info" show>
                Tips! Innloggede brukere kan få tilgang til eksklusive FINN-priser
            </Alert>
            <CarSearchHiddenInputs
                driverAge={state.driverAge}
                pickupDate={state.pickupDate}
                pickupTime={state.pickupTime}
                returnDate={state.returnDate}
                returnTime={state.returnTime}
                pickupLocationId={state.pickupLocationId}
                pickupLocationLatitude={state.pickupLocationLatitude}
                pickupLocationLongitude={state.pickupLocationLongitude}
                pickupLocation={state.pickupLocation}
                returnLocationIsSame={state.returnLocationIsSame}
                returnLocationId={state.returnLocationId}
                returnLocationLatitude={state.returnLocationLatitude}
                returnLocationLongitude={state.returnLocationLongitude}
                returnLocation={state.returnLocation}
                unleash={unleash}
            />
        </SearchPanelWrapper>
    );
};
